import React, { useState } from "react";
import {
  Drawer,
  Empty,
  Spin,
  Steps,
  Badge,
  Button,
  Modal,
  message,
  Tooltip,
} from "antd";
import useUIStore from "../../store/ui";
import { getStatusesData } from "../../constants/main";
import useGetApi from "../../hooks/useGetApi";
import leaveApis from "../../api/methods/leaves";
import useActiveRole from "../../hooks/useActiveRole";
import {
  IconCircleCheckFilled,
  IconProgress,
  IconX,
  IconFileDescription,
  IconFileText,
  IconClock,
  IconCircleCheck,
} from "@tabler/icons-react";
import appColors from "../../theme/common/colors";
import LeaveDetailsCard from "../common/Card/leaveDetails";
import LeaveDetailsModal from "./LeaveDetailsModal";
import { useMutation, useQueryClient } from "@tanstack/react-query";

interface LeaveTrackerProps {
  onClose?: () => void;
}

interface OtherStatusData {
  parent_approved: "accepted" | "rejected" | "pending";
  principal_approved: "accepted" | "rejected" | "pending";
  lea_approved: "accepted" | "rejected" | "pending";
}

const LeaveTracker: React.FC<LeaveTrackerProps> = ({ onClose }) => {
  const queryClient = useQueryClient();
  const [skipModalVisible, setSkipModalVisible] = useState(false);
  const [statusToSkip, setStatusToSkip] = useState<string | null>(null);
  const [detailsModalOpen, setDetailsModalOpen] = useState(false);
  const [notifyingLeaveRequest, setNotifyingLeaveRequest] = useState(false);
  const [notifyingTeacher, setNotifyingTeacher] = useState(false);
  const [notifyingAtc, setNotifyingAtc] = useState(false);
  const [notifyingLea, setNotifyingLea] = useState(false);

  // Get user info for role-based access control
  const { effectiveRole, hasMultipleRoles } = useActiveRole();
  const userRole = effectiveRole;

  const leaveTrackerModal = useUIStore((state) => state.leaveTrackerModal);
  const leaveTrackerData = useUIStore((state) => state.leaveTrackerData);
  const showLeaveTrackerModal = useUIStore(
    (state) => state.showLeaveTrackerModal,
  );
  const closeLeaveTrackerModal = useUIStore(
    (state) => state.closeLeaveTrackerModal,
  );

  // Allow skip for School Admin, LEA Admin, and ATC
  // For multi-capability users, use their active role
  // For single-role users, check their primary role
  const canUserSkip =
    userRole === "School_Admin" ||
    userRole === "LEA_Admin" ||
    userRole === "AttandanceCoordinator";

  // Allow notify for School Admin, LEA Admin, and ATC only (not Teachers or Parents)
  const canUserNotify =
    userRole === "School_Admin" ||
    userRole === "LEA_Admin" ||
    userRole === "AttandanceCoordinator";

  console.log("Skip permission check:", {
    userRole,
    hasMultipleRoles,
    canUserSkip,
    duration: leaveTrackerData?.duration,
    currentStatus: leaveTrackerData?.status,
  });

  const allStatuses = getStatusesData(leaveTrackerData?.duration ?? 0);
  const statuses = Object.keys(allStatuses);
  const {
    data: otherStatus,
    isLoading: isLoadingStatues,
    isError,
    refetch: refetchStatus,
  } = useGetApi(leaveApis(leaveTrackerData?.id).getStatues);

  // Fetch leave details to get tasks
  const { data: leaveDetails } = useGetApi(
    leaveApis(leaveTrackerData?.id).getLeaveDetails,
  ) as {
    data: any;
    isLoading: boolean;
  };

  const otherStatusData: OtherStatusData = otherStatus ? otherStatus[0] : null;
  const tasks = leaveDetails?.task || [];

  // Calculate the effective display status based on task statuses (frontend only, doesn't change DB)
  const getEffectiveStatus = () => {
    const currentStatus = leaveTrackerData?.status;

    if (!tasks.length || !currentStatus) {
      return currentStatus;
    }

    // Get the index of current DB status in the workflow
    const currentStatusIndex = statuses.indexOf(currentStatus);

    const hasAssignedTask = tasks.some(
      (task: any) => task.status === "StudentReview",
    );
    const hasUnderReviewTask = tasks.some(
      (task: any) => task.status === "UnderReview",
    );
    const hasCompletedTask = tasks.some(
      (task: any) => task.status === "AttendanceCoordinatorReview",
    );

    // Determine task-based status
    let taskBasedStatus = currentStatus;
    if (hasCompletedTask) {
      taskBasedStatus = "AttendanceCoordinatorReview";
    } else if (hasUnderReviewTask) {
      taskBasedStatus = "UnderReview";
    } else if (hasAssignedTask && currentStatus === "IS_Requested") {
      taskBasedStatus = "StudentReview";
    }

    // Get the index of task-based status
    const taskBasedStatusIndex = statuses.indexOf(taskBasedStatus);

    // Only use task-based status if it's further ahead than current DB status
    // This prevents showing old task status when DB status has moved forward
    if (taskBasedStatusIndex > currentStatusIndex) {
      return taskBasedStatus;
    }

    // Otherwise return the actual DB status (it's further ahead or equal)
    return currentStatus;
  };

  const effectiveStatus = getEffectiveStatus();
  const currentStep = statuses.indexOf(effectiveStatus ?? "") || 0;

  console.log("leaveTrackerData", leaveTrackerData);
  console.log("leaveDetails with tasks", leaveDetails);
  console.log("Effective status for display:", {
    actualStatus: leaveTrackerData?.status,
    effectiveStatus,
    currentStep,
  });

  // Determine which statuses can be skipped based on duration
  // These are the statuses where the skip button should appear
  const getSkippableStatuses = () => {
    const duration = Number(leaveTrackerData?.duration) || 0;

    console.log("Skippable statuses calculation:", {
      rawDuration: leaveTrackerData?.duration,
      parsedDuration: duration,
      isMoreThan10: duration >= 10,
    });

    if (duration >= 10) {
      // For >= 10 days: Can skip waiting for Principal Approval, LEA Approval, ATC verification, LEA Review
      return [
        "Principal_Approval",
        "LEA_Approval",
        "AttendanceCoordinatorReview",
        "LEAReview",
      ];
    } else {
      // For < 10 days: Can skip waiting for ATC verification and LEA Review
      return ["AttendanceCoordinatorReview", "LEAReview"];
    }
  };

  const skippableStatuses = getSkippableStatuses();

  console.log("Skippable statuses:", skippableStatuses);

  // Get next status in the workflow
  const getNextStatus = (currentStatus: string): string | null => {
    const currentIndex = statuses.indexOf(currentStatus);
    if (currentIndex >= 0 && currentIndex < statuses.length - 1) {
      return statuses[currentIndex + 1];
    }
    return null;
  };

  // Skip status mutation
  const { mutate: skipStatus, isPending: isSkipping } = useMutation({
    mutationFn: leaveApis(leaveTrackerData?.id).skipLeaveStatus.mf,
    onSuccess: (_data, variables) => {
      setSkipModalVisible(false);

      // Update the leaveTrackerData with new status
      if (leaveTrackerData) {
        showLeaveTrackerModal({
          ...leaveTrackerData,
          status: variables, // The new status we just skipped to
        });
      }

      // Invalidate all leave-related queries to refresh data
      queryClient.invalidateQueries({ queryKey: ["getFilteredLeaves"] });
      queryClient.invalidateQueries({ queryKey: ["Get-list-leave-requests"] });

      // Dispatch custom event to notify parent components to refetch
      window.dispatchEvent(
        new CustomEvent("leaveStatusUpdated", {
          detail: { id: leaveTrackerData?.id, newStatus: variables },
        }),
      );

      // Refetch the status to get updated approval states
      refetchStatus();
    },
    onError: () => {
      message.error("Failed to update status");
    },
  });

  // Notify leave request mutation (when status is IS_Requested for <10 days)
  const { mutate: notifyLeaveRequest } = useMutation({
    mutationFn: leaveApis(leaveTrackerData?.id).notifyLeaveRequest.mf,
    onSuccess: () => {
      setNotifyingLeaveRequest(false);
    },
    onError: () => {
      message.error("Failed to send notification");
      setNotifyingLeaveRequest(false);
    },
  });

  // Notify teacher mutation (when status is StudentReview for >=10 days)
  const { mutate: notifyTeacher } = useMutation({
    mutationFn: leaveApis(leaveTrackerData?.id).notifyTeacher.mf,
    onSuccess: () => {
      setNotifyingTeacher(false);
    },
    onError: () => {
      message.error("Failed to send teacher notification");
      setNotifyingTeacher(false);
    },
  });

  // Notify ATC mutation (when status is AttendanceCoordinatorReview)
  const { mutate: notifyAtc } = useMutation({
    mutationFn: leaveApis(leaveTrackerData?.id).notifyAtc.mf,
    onSuccess: () => {
      setNotifyingAtc(false);
    },
    onError: () => {
      message.error("Failed to send ATC notification");
      setNotifyingAtc(false);
    },
  });

  // Notify LEA Admin mutation (when status is LEAReview)
  const { mutate: notifyLea } = useMutation({
    mutationFn: leaveApis(leaveTrackerData?.id).notifyLea.mf,
    onSuccess: () => {
      setNotifyingLea(false);
    },
    onError: () => {
      message.error("Failed to send LEA notification");
      setNotifyingLea(false);
    },
  });

  const handleSkipClick = () => {
    // Use effectiveStatus for calculating next step, not the DB status
    const nextStatus = getNextStatus(effectiveStatus ?? "");
    if (nextStatus) {
      setStatusToSkip(nextStatus);
      setSkipModalVisible(true);
    }
  };

  const handleSkipConfirm = () => {
    if (statusToSkip) {
      skipStatus(statusToSkip);
    }
  };

  const handleNotifyLeaveRequest = () => {
    setNotifyingLeaveRequest(true);
    notifyLeaveRequest();
  };

  const handleNotifyTeacher = () => {
    setNotifyingTeacher(true);
    notifyTeacher();
  };

  const handleNotifyAtc = () => {
    setNotifyingAtc(true);
    notifyAtc();
  };

  const handleNotifyLea = () => {
    setNotifyingLea(true);
    notifyLea();
  };

  // Render task status icon with tooltip
  const renderTaskIcon = (task: any) => {
    const getTaskIcon = () => {
      switch (task.status) {
        case "StudentReview":
          return (
            <IconFileText size={24} color={appColors.primary} stroke={1.5} />
          );
        case "UnderReview":
          return <IconClock size={24} color={appColors.yellow} stroke={1.5} />;
        case "AttendanceCoordinatorReview":
          return (
            <IconCircleCheck size={24} color={appColors.green} stroke={1.5} />
          );
        default:
          return (
            <IconFileText
              size={24}
              color={appColors.gray}
              stroke={1.5}
              className="opacity-50"
            />
          );
      }
    };

    const getStatusText = () => {
      switch (task.status) {
        case "StudentReview":
          return "Assigned - Waiting for student";
        case "UnderReview":
          return "Under Review - Waiting for teacher approval";
        case "AttendanceCoordinatorReview":
          return "Completed - Teacher approved";
        default:
          return "Unknown status";
      }
    };

    const formatDate = (dateStr: string) => {
      if (!dateStr) return "N/A";
      try {
        return new Date(dateStr).toLocaleDateString("en-US", {
          month: "short",
          day: "numeric",
          year: "numeric",
        });
      } catch {
        return dateStr;
      }
    };

    return (
      <Tooltip
        key={task.id}
        title={
          <div className="text-sm">
            <div className="font-semibold mb-1">{task.assignmnet_title}</div>
            <div className="text-xs opacity-90">Status: {getStatusText()}</div>
            <div className="text-xs opacity-90">
              Assignment Date: {formatDate(task.assignment_day)}
            </div>
            {task.submission_date && (
              <div className="text-xs opacity-90">
                Due: {formatDate(task.submission_date)}
              </div>
            )}
            {task.assignment_details && (
              <div className="text-xs opacity-90 mt-1">
                {task.assignment_details.substring(0, 100)}
                {task.assignment_details.length > 100 && "..."}
              </div>
            )}
          </div>
        }
        placement="top"
      >
        <div className="cursor-pointer hover:scale-250 transition-transform inline-block">
          {getTaskIcon()}
        </div>
      </Tooltip>
    );
  };

  const statusCard = (
    status: "accepted" | "rejected" | "pending",
    title: string,
    desc: string,
  ) => {
    const getStatusBadge = () => {
      if (status === "accepted") {
        return (
          <Badge status="success" text="Approved" className="font-medium" />
        );
      }
      if (status === "rejected") {
        return <Badge status="error" text="Rejected" className="font-medium" />;
      }
      return <Badge status="warning" text="Pending" className="font-medium" />;
    };

    const getIcon = () => {
      if (status === "accepted") {
        return <IconCircleCheckFilled size={24} fill={appColors.green} />;
      }
      if (status === "rejected") {
        return <IconX size={24} fill={appColors.red} />;
      }
      return <IconProgress size={24} fill={appColors.yellow} />;
    };

    return (
      <div className="flex flex-row mt-2 gap-2 bg-slate-100 px-2 py-2 rounded-xl">
        {getIcon()}
        <div className="flex-1">
          <div className="flex items-center justify-between">
            <p className="font-semibold text-[16px]">{title}</p>
            {getStatusBadge()}
          </div>
          <p className="text-sm text-gray-600 mt-1">{desc}</p>
        </div>
      </div>
    );
  };

  return (
    <Drawer
      width={600}
      title="Leave Tracker"
      open={leaveTrackerModal}
      onClose={() => {
        onClose?.();
        closeLeaveTrackerModal();
      }}
      footer={null}
    >
      {isLoadingStatues ? (
        <Spin />
      ) : isError ? (
        <Empty description="Failed to load data" />
      ) : (
        <div>
          <LeaveDetailsCard record={leaveTrackerData} />

          {/* View Full Details Button */}
          <Button
            type="primary"
            size="large"
            block
            icon={<IconFileDescription size={20} />}
            className="mt-4 mb-4"
            onClick={() => setDetailsModalOpen(true)}
          >
            View Full Leave Details
          </Button>

          <div className="border p-2 border-slate-300 rounded-xl mb-4 mt-4 ">
            <p className="mb-2 ml-2 font-semibold text-gray-700">
              Approvals Required
            </p>
            {statusCard(
              otherStatusData?.principal_approved,
              "Principal Approval",
              "Principal needs to review and approve the IS request.",
            )}
            {statusCard(
              otherStatusData?.parent_approved,
              "Parent Acceptance",
              "Parent needs to review and accept the assigned objectives.",
            )}
          </div>
          <div className="border p-2 border-slate-300 rounded-xl mb-4 ">
            <p className="mb-2 ml-2 font-semibold text-gray-700">Progress</p>

            <Steps
              className="mx-2"
              progressDot
              current={currentStep + 1}
              status="process"
              direction="vertical"
              items={statuses.map((key, index) => {
                // This is the next step after current (for display indicator)
                const isNextStep = index === currentStep + 1;
                // Show skip button on the waiting step (isNextStep for display)
                // But check if the CURRENT status (what we're waiting for) is skippable
                const currentStatusKey = statuses[currentStep];
                const canSkip =
                  canUserSkip &&
                  isNextStep &&
                  skippableStatuses.includes(currentStatusKey);

                if (isNextStep) {
                  console.log(`Waiting step display (${key}) skip check:`, {
                    index,
                    currentStep,
                    currentStatusKey,
                    isNextStep,
                    canUserSkip,
                    isSkippable: skippableStatuses.includes(currentStatusKey),
                    canSkip,
                  });
                }

                // Determine which title to show based on step status
                // Since current={currentStep + 1}, the indicator is on the waiting step
                const stepStatus =
                  index <= currentStep
                    ? "finish"
                    : index === currentStep + 1
                      ? "process"
                      : "wait";
                const displayTitle =
                  isNextStep && allStatuses[key].waitingTitle
                    ? allStatuses[key].waitingTitle
                    : allStatuses[key].title;
                const displayDescription =
                  isNextStep && allStatuses[key].waitingDescription
                    ? allStatuses[key].waitingDescription
                    : allStatuses[key].description;

                // Determine which notify button to show based on current status and duration
                const duration = Number(leaveTrackerData?.duration) || 0;

                // For <10 days: Show notify when waiting for StudentReview (teacher assignment)
                // For >=10 days: Show notify when waiting for StudentReview (teacher assignment)
                const showNotifyLeaveRequest =
                  duration < 10 && isNextStep && key === "StudentReview";
                const showNotifyTeacher =
                  duration >= 10 && isNextStep && key === "StudentReview";
                const showNotifyAtc = isNextStep && key === "LEAReview";
                const showNotifyLea = isNextStep && key === "Completed";

                // Show notify button on the current waiting step (only for authorized users)
                const shouldShowNotifyButton =
                  canUserNotify &&
                  (showNotifyLeaveRequest ||
                    showNotifyTeacher ||
                    showNotifyAtc ||
                    showNotifyLea);

                return {
                  title: (
                    <div className="flex items-center justify-between w-full">
                      <span>{displayTitle}</span>
                      <div className="flex gap-2">
                        {shouldShowNotifyButton && showNotifyLeaveRequest && (
                          <Button
                            size="small"
                            type="default"
                            onClick={handleNotifyLeaveRequest}
                            loading={notifyingLeaveRequest}
                          >
                            Notify
                          </Button>
                        )}
                        {shouldShowNotifyButton && showNotifyTeacher && (
                          <Button
                            size="small"
                            type="default"
                            onClick={handleNotifyTeacher}
                            loading={notifyingTeacher}
                          >
                            Notify
                          </Button>
                        )}
                        {shouldShowNotifyButton && showNotifyAtc && (
                          <Button
                            size="small"
                            type="default"
                            onClick={handleNotifyAtc}
                            loading={notifyingAtc}
                          >
                            Notify ATC
                          </Button>
                        )}
                        {shouldShowNotifyButton && showNotifyLea && (
                          <Button
                            size="small"
                            type="default"
                            onClick={handleNotifyLea}
                            loading={notifyingLea}
                          >
                            Notify LEA
                          </Button>
                        )}
                        {canSkip && (
                          <Button
                            size="small"
                            className="ml-3"
                            onClick={handleSkipClick}
                            loading={isSkipping}
                          >
                            Skip
                          </Button>
                        )}
                      </div>
                    </div>
                  ),
                  description: (
                    <div>
                      <div>{displayDescription}</div>
                      {/* Show tasks when on StudentReview step (Waiting for Assignment) */}
                      {key === "StudentReview" && tasks.length > 0 && (
                        <div className="mt-3 bg-slate-100 rounded-lg py-1 px-3 mr-5 flex flex-wrap gap-3 items-center">
                          <span className="text-md font-medium text-gray-600">
                            Tasks ({tasks.length}):
                          </span>
                          {tasks.map((task: any) => (
                            <div className="mt-2" key={task.id}>
                              {renderTaskIcon(task)}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  ),
                  status: stepStatus,
                };
              })}
            />
          </div>

          {/* Skip Confirmation Modal */}
          <Modal
            title="Skip Current Step?"
            open={skipModalVisible}
            onOk={handleSkipConfirm}
            onCancel={() => setSkipModalVisible(false)}
            okText="Yes, Skip"
            cancelText="Cancel"
            confirmLoading={isSkipping}
          >
            <p className="mb-3">
              Are you sure you want to skip the current step and move to the
              next one?
            </p>
            <p className="text-sm text-gray-600">
              <strong>Current:</strong>{" "}
              {allStatuses[effectiveStatus ?? ""]?.title}
            </p>
            {statusToSkip && (
              <p className="text-sm text-gray-600">
                <strong>Next:</strong> {allStatuses[statusToSkip]?.title}
              </p>
            )}
            <p className="text-sm text-amber-600 mt-3">
              ⚠️ This action cannot be undone.
            </p>
          </Modal>
        </div>
      )}

      {/* Leave Details Modal */}
      <LeaveDetailsModal
        leaveId={leaveTrackerData?.id ?? null}
        open={detailsModalOpen}
        onClose={() => setDetailsModalOpen(false)}
      />
    </Drawer>
  );
};

export default LeaveTracker;
